import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import TypeAhead from './TypeAhead';
import { FieldDefinition } from '../contexts/FieldDefinitionsContext';
import {
  getOperatorsForField,
  getFieldTypeFromDefinitions,
  getFilterPlaceholder,
  isDynamicDateValue,
  parseBetweenValue,
  formatBetweenValue,
  formatTimeFilterValue,
  parseTimeFilterValue,
  parseLocalDateValue,
  reconcileValueForOperator,
} from '../utils/dynamicColumns';
import DateTimePicker, { DateTimeValue } from './DateTimePicker';
import { DynamicDatePicker, DynamicDateRangePicker } from './DynamicDatePicker';

/**
 * Shared filter-condition primitives — a single field/operator/value row plus the
 * type-aware value inputs. Extracted from FirstCardModal so the Dispatch/LC
 * dashboards and the order-management/quotes Custom Filter render an identical
 * builder and stay in sync. `field` is a field_definitions.fieldname; operators
 * come from getOperatorsForField; `values` is the serialized value(s).
 */
export interface FilterCondition {
  id: number;
  field: string;
  compare: string;
  values: string;
}

/**
 * DateValueInput - Handles date/datetime fields with toggle between specific and relative modes
 */
function DateValueInput({
  condition,
  fieldType,
  updateCondition,
}: {
  condition: FilterCondition;
  fieldType: 'date' | 'datetime';
  updateCondition: (id: number, field: keyof FilterCondition, value: string) => void;
}) {
  const compare = condition.compare;
  const isBetween = compare === 'Between';

  // Equality and Between on a datetime field compare the date part only — the
  // server expands the value to whole calendar days — so don't collect a time
  // we'd ignore. Between especially: the range picker defaults its end time to
  // NOON, so an untouched range silently cut the last day in half.
  const pickerType: 'date' | 'datetime' =
    fieldType === 'datetime' &&
    (compare === 'Equal to' || compare === 'Not equal to' || compare === 'Between')
      ? 'date'
      : fieldType;

  // Determine if current value is dynamic (starts with @)
  const valueIsDynamic = isDynamicDateValue(condition.values) ||
    (isBetween && condition.values.includes('@'));

  const [mode, setMode] = React.useState<'specific' | 'relative'>(
    valueIsDynamic ? 'relative' : 'specific'
  );

  // Sync mode when value changes externally (e.g., loading saved filter)
  React.useEffect(() => {
    const isDynamic = isDynamicDateValue(condition.values) ||
      (isBetween && condition.values.includes('@'));
    setMode(isDynamic ? 'relative' : 'specific');
  }, [condition.values, isBetween]);

  // Handle mode toggle
  const handleModeChange = (newMode: 'specific' | 'relative') => {
    setMode(newMode);
    // Clear value when switching modes
    if (newMode === 'relative') {
      if (isBetween) {
        updateCondition(condition.id, 'values', '@today-7d to @today');
      } else {
        updateCondition(condition.id, 'values', '@today');
      }
    } else {
      updateCondition(condition.id, 'values', '');
    }
  };

  return (
    <div className="w-full space-y-2">
      {/* Mode toggle */}
      <div className="flex items-center gap-1 text-xs">
        <button
          type="button"
          onClick={() => handleModeChange('specific')}
          className={`px-2 py-1 rounded transition-colors ${
            mode === 'specific'
              ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-medium'
              : 'text-muted hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          Specific Date
        </button>
        <button
          type="button"
          onClick={() => handleModeChange('relative')}
          className={`px-2 py-1 rounded transition-colors ${
            mode === 'relative'
              ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-medium'
              : 'text-muted hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          Relative Date
        </button>
      </div>

      {/* Value input based on mode */}
      {mode === 'relative' ? (
        // Dynamic date picker
        isBetween ? (
          <DynamicDateRangePicker
            value={condition.values || '@today-7d to @today'}
            onChange={(val) => updateCondition(condition.id, 'values', val)}
          />
        ) : (
          <DynamicDatePicker
            value={condition.values || '@today'}
            onChange={(val) => updateCondition(condition.id, 'values', val)}
            includeTime={pickerType === 'datetime'}
          />
        )
      ) : (
        // Static date picker
        isBetween ? (
          <DateTimePicker
            mode={pickerType}
            selectionType="range"
            value={parseBetweenValue(condition.values)}
            onChange={(val) => {
              const formatted = formatBetweenValue(val as DateTimeValue | null, pickerType === 'datetime');
              updateCondition(condition.id, 'values', formatted);
            }}
            placeholder={`Select ${pickerType} range...`}
            inputVariant="modal"
            usePortal
            labelType="none"
            autoFlip
          />
        ) : (
          <DateTimePicker
            mode={pickerType}
            selectionType="single"
            // Parsed as LOCAL time — the same helper the range picker uses, so
            // both read back exactly the day the user picked.
            value={parseLocalDateValue(condition.values)}
            onChange={(val) => {
              if (val instanceof Date) {
                // Format as local time, not UTC
                const year = val.getFullYear();
                const month = String(val.getMonth() + 1).padStart(2, '0');
                const day = String(val.getDate()).padStart(2, '0');

                if (pickerType === 'datetime') {
                  const hours = String(val.getHours()).padStart(2, '0');
                  const minutes = String(val.getMinutes()).padStart(2, '0');
                  updateCondition(condition.id, 'values', `${year}-${month}-${day}T${hours}:${minutes}`);
                } else {
                  updateCondition(condition.id, 'values', `${year}-${month}-${day}`);
                }
              } else {
                updateCondition(condition.id, 'values', '');
              }
            }}
            placeholder={`Select ${pickerType}...`}
            inputVariant="modal"
            usePortal
            labelType="none"
            autoApply={pickerType=='date'}
            autoFlip
          />
        )
      )}
    </div>
  );
}

/**
 * TimeValueInput - Handles time fields (HH:MM strings, e.g. appt times) with the
 * shared DateTimePicker in 24-hour time mode
 */
function TimeValueInput({
  condition,
  updateCondition,
}: {
  condition: FilterCondition;
  updateCondition: (id: number, field: keyof FilterCondition, value: string) => void;
}) {
  const isBetween = condition.compare === 'Between';

  if (isBetween) {
    // Parse "HH:MM to HH:MM" into a range value for the picker
    const [startStr, endStr] = condition.values.includes(' to ')
      ? condition.values.split(' to ', 2)
      : ['', ''];
    const startDate = parseTimeFilterValue(startStr) ?? undefined;
    const endDate = parseTimeFilterValue(endStr) ?? undefined;

    return (
      <DateTimePicker
        mode="time"
        selectionType="range"
        use24Hour
        value={startDate && endDate ? { startDate, endDate } : null}
        onChange={(val) => {
          const range = val as DateTimeValue | null;
          if (range?.startDate && range?.endDate) {
            updateCondition(
              condition.id,
              'values',
              `${formatTimeFilterValue(range.startDate)} to ${formatTimeFilterValue(range.endDate)}`
            );
          } else {
            updateCondition(condition.id, 'values', '');
          }
        }}
        placeholder="Select time range..."
        inputVariant="modal"
        usePortal
        labelType="none"
        autoFlip
      />
    );
  }

  return (
    <DateTimePicker
      mode="time"
      selectionType="single"
      use24Hour
      value={parseTimeFilterValue(condition.values)}
      onChange={(val) => {
        if (val instanceof Date) {
          updateCondition(condition.id, 'values', formatTimeFilterValue(val));
        } else {
          updateCondition(condition.id, 'values', '');
        }
      }}
      placeholder="Select time..."
      inputVariant="modal"
      usePortal
      labelType="none"
      autoFlip
    />
  );
}

/** Render the appropriate value input based on field type and operator */
export function renderFilterValueInput(
  condition: FilterCondition,
  fieldDefinitions: FieldDefinition[],
  updateCondition: (id: number, field: keyof FilterCondition, value: string) => void
): React.ReactNode {
  const fieldType = getFieldTypeFromDefinitions(condition.field, fieldDefinitions);
  const compare = condition.compare;

  // Find the field definition to check for enum options
  const fieldDef = fieldDefinitions.find(def => def.fieldname === condition.field);
  const enumOptions = fieldDef?.enum_options;

  // No input needed for empty checks
  if (compare === 'Is empty' || compare === 'Is not empty') {
    return (
      <div className="w-full px-4 py-2.5 text-sm text-gray-400 dark:text-gray-500 italic bg-gray-50 dark:bg-gray-700 rounded-lg">
        No value needed
      </div>
    );
  }

  // Enum fields with options - render multi-select typeahead
  if (enumOptions && enumOptions.length > 0) {
    // Parse current values (comma-separated) into array
    const selectedValues = condition.values
      ? condition.values.split(',').map(v => v.trim()).filter(v => v)
      : [];

    return (
      <TypeAhead
        options={enumOptions.map(opt => ({
          value: opt,
          label: opt,
        }))}
        multiple={true}
        value={selectedValues}
        onChange={(values: string[]) => {
          // Update the condition with comma-separated values
          updateCondition(condition.id, 'values', values.join(', '));
        }}
        placeholder="Select values..."
        showSearch={true}
        className="w-full"
        chipsDirection="down"
      />
    );
  }

  // Boolean fields get a dropdown
  if (fieldType === 'boolean' || fieldType === 'nullable_boolean') {
    return (
      <select
        value={condition.values}
        onChange={(e) => updateCondition(condition.id, 'values', e.target.value)}
        className="w-full px-4 py-2.5 bg-panel border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-body focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
      >
        <option value="">Select...</option>
        <option value="Y">Yes</option>
        <option value="N">No</option>
      </select>
    );
  }

  // Date/Datetime fields - use the DateValueInput component with mode toggle
  if (fieldType === 'date' || fieldType === 'datetime') {
    return (
      <DateValueInput
        condition={condition}
        fieldType={fieldType}
        updateCondition={updateCondition}
      />
    );
  }

  // Time fields (HH:MM) - use the DateTimePicker in 24-hour time mode
  if (fieldType === 'time') {
    return (
      <TimeValueInput
        condition={condition}
        updateCondition={updateCondition}
      />
    );
  }

  // Number fields get text input with decimal keyboard on mobile
  if ((fieldType === 'number' || fieldType === 'decimal') && compare !== 'Between') {
    return (
      <input
        type="text"
        inputMode="decimal"
        value={condition.values}
        onChange={(e) => updateCondition(condition.id, 'values', e.target.value)}
        placeholder={getFilterPlaceholder(fieldType, compare)}
        className="w-full px-4 py-2.5 bg-panel border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-body placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
      />
    );
  }

  // Default: text input with appropriate placeholder
  return (
    <input
      type="text"
      value={condition.values}
      onChange={(e) => updateCondition(condition.id, 'values', e.target.value)}
      placeholder={getFilterPlaceholder(fieldType, compare)}
      className="w-full px-4 py-2.5 bg-panel border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-body placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
    />
  );
}

interface FilterConditionRowProps {
  condition: FilterCondition;
  fieldDefinitions: FieldDefinition[];
  loadingFields?: boolean;
  onUpdate: (id: number, field: keyof FilterCondition, value: string) => void;
  onRemove: (id: number) => void;
  removeDisabled?: boolean;
  /** Put the cursor in the field picker when the row mounts (first row of a
   *  freshly-opened builder, or a row just added by tabbing off the previous one). */
  autoFocus?: boolean;
  /** Fires when the user presses Tab (forward) out of the value input. The host
   *  uses it on the last row to append a new row and focus it, so a filter can be
   *  built entirely from the keyboard instead of click Add Filter, click field. */
  onTabOut?: () => void;
}

/**
 * A single filter-condition row: field picker + operator picker + type-aware
 * value input + remove button. Shared by FirstCardModal and the quotes Custom
 * Filter so both render the same builder.
 */
export const FilterConditionRow: React.FC<FilterConditionRowProps> = ({
  condition,
  fieldDefinitions,
  loadingFields = false,
  onUpdate,
  onRemove,
  removeDisabled = false,
  autoFocus = false,
  onTabOut,
}) => (
  <div className="flex gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded border border-rule hover:border-blue-400 dark:hover:border-blue-500 transition-colors">
    <div className="flex-1 flex gap-2">
      <div className="w-80 flex items-center pt-[3px]">
        <TypeAhead
          options={fieldDefinitions.map((def) => ({
            value: def.fieldname,
            label: def.displayname,
            description: def.abbreviation || undefined,
          }))}
          value={condition.field}
          onChange={(value) => onUpdate(condition.id, 'field', value)}
          placeholder={loadingFields ? 'Loading fields...' : 'Search field...'}
          disabled={loadingFields}
          showSearch={true}
          showDescriptions={true}
          className="w-full"
          autoFocus={autoFocus}
        />
      </div>

      <div className="w-48 flex items-center pt-[3px]">
        <TypeAhead
          options={getOperatorsForField(condition.field, fieldDefinitions).map((op: string) => ({
            value: op,
            label: op,
          }))}
          value={condition.compare}
          onChange={(value) => {
            // "Between" stores "<start> to <end>", every other operator a single
            // value. Reshape before the switch so the server never receives a
            // value the new operator can't read.
            const reconciled = reconcileValueForOperator(
              condition.values, value, condition.compare
            );
            if (reconciled !== condition.values) {
              onUpdate(condition.id, 'values', reconciled);
            }
            onUpdate(condition.id, 'compare', value);
          }}
          placeholder="Select operator..."
          showSearch={true}
          className="w-full"
        />
      </div>

      <div
        className="flex-1"
        data-testid="filter-value"
        onKeyDown={(e) => {
          if (!onTabOut || e.key !== 'Tab' || e.shiftKey || e.defaultPrevented) return;
          // Only for the value control itself — a keypress inside a portal-mounted
          // date picker popover bubbles here through the React tree but isn't the
          // user leaving the row.
          if (!e.currentTarget.contains(e.target as Node)) return;
          e.preventDefault();
          onTabOut();
        }}
      >
        {renderFilterValueInput(condition, fieldDefinitions, onUpdate)}
      </div>
    </div>

    <button
      type="button"
      onClick={() => onRemove(condition.id)}
      disabled={removeDisabled}
      className={`p-1 mt-[3px] text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded transition-colors self-start ${
        removeDisabled ? 'opacity-30 cursor-not-allowed' : ''
      }`}
    >
      <XMarkIcon className="w-4 h-4" />
    </button>
  </div>
);

export default FilterConditionRow;
